import {
  Component,
  Inject,
  OnInit
} from '@angular/core';
import {LEADS_DATA_SERVICE, LeadsDataService} from '../../../services/leads/leads-data.service';
import {ActivatedRoute, Router} from '@angular/router';
import {ActionHelper} from '../../../helper/action.helper';
import {MatDialog} from '@angular/material/dialog';
import {LeadData} from '../../../models/leads/leads.model';
import {ACTION_DATA_SERVICE, ActionDataService} from '../../../services/action/data.service';
import {DateTimeHelper} from '../../../helper/date-time.helper';
import {UserProfileShareService} from '../../../services/shared-service/user-profile-share.service';
import {filter, take} from 'rxjs/operators';

@Component({
  templateUrl: './request-invitation-preview.component.html',
  styleUrls: ['./request-invitation-preview.component.scss'],
  standalone: false,
})
export class RequestInvitationPreviewComponent implements OnInit {
  private mainLeadId: number;
  private leadData: LeadData;

  private rejectText = 'Anfrage ablehnen';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    @Inject(LEADS_DATA_SERVICE) private leadsDataService: LeadsDataService,
    @Inject(ACTION_DATA_SERVICE) public actionDataService: ActionDataService,
    private sharedProfileService: UserProfileShareService,
  ) {
  }


  ngOnInit(): void {
    window.scroll({top: 0});

    this.mainLeadId = +this.route.snapshot.paramMap.get('leadId');

    this.actionDataService.getSnippets().subscribe(data => {
      this.rejectText = data.data?.find(item => item.Name === "LEAD_REJECT")?.Text ?? this.rejectText;
    });

    this.getMainLead();
  }

  getMainLead(): void {
    this.sharedProfileService.getProfile().pipe(
      filter(profile => !!profile),
      take(1),
    ).subscribe(profile => {
      const userId = profile.User?.Id;
      const isDealerPartner = this.sharedProfileService.computeIsDealerPartner(profile.Tenants ?? []);

      // Finstral users never see the accept overlay, the detail page handles their access
      if (!isDealerPartner) {
        this.goToDetailPage();
        return;
      }

      this.leadsDataService.getLeadById(this.mainLeadId).subscribe(({success, data, httpStatus, apiStatus}) => {
        if (!success) {
          if (httpStatus === 404 || (httpStatus === 400 && (apiStatus === 404 || apiStatus === 403))) {
            this.goToNotAllowedPage();
          } else {
            this.showAlertDialog();
          }
          return;
        }

        this.leadData = data;

        // No enquiry info: either someone else accepted first (too late) or no read permission
        if (!this.leadData?.Lead) {
          const brokeringInfo = this.leadData?.BrokeringInfo;
          if (brokeringInfo?.FirstAcceptedDate || brokeringInfo?.LastAcceptedDate) {
            this.showBrokerAlreadyAcceptedDialog();
          } else {
            this.goToNotAllowedPage();
          }
          return;
        }

        if (this.leadData.BrokeringInfo?.CanAccept) {
          const showWarning = this.leadData.BrokeringInfo.NumberAccepted !== 0;
          this.showBrokerAcceptEnquiryDialog(showWarning);
          return;
        }

        // Only the responsible user of the enquiry may accept it
        const isResponsible = +this.leadData.Lead.EditorId === +userId;
        if (this.leadData.Lead.RequiresConfirmation && isResponsible) {
          this.showAcceptEnquiryDialog();
        } else {
          this.goToDetailPage();
        }
      });
    });
  }

  // --- OLD: first 100 leads + find ----------------------------------------
  // To compare: comment out getMainLead() above and uncomment this one.
  // getMainLead(): void {
  //   this.leadsDataService.getLeads(false, true, false, false, {
  //     pageNumber: 0,
  //     pageSize: 100,
  //     sort: ['-RequiresConfirmation'],
  //   }).subscribe(({success, data}) => {
  //     if (success) {
  //       this.leadData = data.find((item) => item.Lead.Id === this.mainLeadId);
  //       if (!this.leadData) {
  //         this.showAlertDialog();
  //         return;
  //       }
  //       const requiresConfirmation = this.leadData.Lead.RequiresConfirmation;
  //       if (requiresConfirmation) {
  //         this.showAcceptEnquiryDialog();
  //       } else {
  //         this.goToDetailPage();
  //       }
  //     }
  //   });
  // }


  showAcceptEnquiryDialog(): void {
    ActionHelper.showAction('DELETE-CONFIRM', this.dialog, {
      customMessageSnippet: 'REQUEST_DETAIL_SKELETON_ACCEPT_ENQUIRY_HEADLINE',
      customDescriptionSnippet: 'REQUEST_DETAIL_SKELETON_ACCEPT_ENQUIRY_TEXT',
      customConfirmSnippet: 'REQUEST_OVERVIEW_ACTION_ACCEPT_ENQUIRY',
      customCancelSnippet: 'REQUEST_OVERVIEW_ACTION_DECLINE_ENQUIRY',
    })?.afterClosed().subscribe(isAccepted => {
      if (isAccepted) {
        this.confirmAssignment();
      } else {
        this.rejectAssignment();
      }
    });
  }

  showAlertDialog(): void {
    ActionHelper.showAction('ALERT-DIALOG', this.dialog, {
      titleSnippet: 'REQUEST_DETAIL_SKELETON_ENQUIRY_ALREADY_ACCEPTED',
      descriptionSnippet: 'REQUEST_OVERVIEW_MESSAGE_ENQUIRY_ALREADY_ACCEPTED',
      confirmButtonSnippet: 'REQUEST_DETAIL_SKELETON_ACCEPT_ENQUIRY_LINK_OVERVIEW',
      confirmButtonClass: '',
      confirmButtonIcon: '/assets/icons/arrow-short.svg',
    })?.afterClosed().subscribe(() => {
      this.goToHomePage();
    });
  }


  rejectAssignment(): void {
    const actionData = {
      contactName: this.leadData.Lead.Contact.FirstName + ' ' + this.leadData.Lead.Contact.LastName,
      leadId: this.leadData.Lead.Id,
      title: this.rejectText
    }
    ActionHelper.showAction('LEAD_REJECT', this.dialog, actionData)?.afterClosed().subscribe(data => {
      if (data === 'remove') {
        this.goToHomePage();
      } else {
        this.showAcceptEnquiryDialog();
      }
    });
  }

  confirmAssignment(): void {
    const lead = this.leadData;

    this.actionDataService.confirmRequest(lead.Lead.Id).subscribe(({success, data}) => {
      if (success) {
        if (!lead?.Lead?.Read) {
          this.leadsDataService.markAsRead(this.mainLeadId).subscribe(({success}) => {
            if (success) {
              this.goToDetailPage();
            }
          });
        } else {
          this.goToDetailPage();
        }
      } else {
        this.goToHomePage();
      }
    });
  }

  showBrokerAcceptEnquiryDialog(showWarning: boolean): void {
    ActionHelper.showAction('BROKER-ACCEPT-CONFIRM', this.dialog, {showWarning: showWarning})?.afterClosed()
      .subscribe(isAccepted => {
        if (isAccepted) {
          this.confirmAssignment();
        } else {
          this.rejectAssignment();
        }
      });
  }

  showBrokerAlreadyAcceptedDialog(): void {
    ActionHelper.showAction('BROKER-ACCEPT-CONFIRM', this.dialog, {
      alreadyAccepted: true,
      acceptedDate: this.getBrokerAcceptedDate(),
      acceptedTime: this.getBrokerAcceptedTime(),
    })?.afterClosed().subscribe(_ => {
      this.goToHomePage();
    });
  }

  private goToDetailPage(): void {
    this.router.navigate(['/details/' + this.mainLeadId], {replaceUrl: true});
  }

  private goToNotAllowedPage(): void {
    this.router.navigate(['/not-allowed-details'], {replaceUrl: true});
  }

  private goToHomePage(): void {
    this.router.navigate(['/'], {replaceUrl: true});
  }

  private getBrokerAcceptedDate(): string {
    const acceptedDate = this.getBrokerAcceptedDateString();

    return acceptedDate ? DateTimeHelper.getFormattedDateOnly(acceptedDate) : '';
  }

  private getBrokerAcceptedTime(): string {
    const acceptedDate = this.getBrokerAcceptedDateString();

    return acceptedDate ? DateTimeHelper.getFormattedTimeOnly(acceptedDate) : '';
  }

  private getBrokerAcceptedDateString(): string {
    const acceptedDate = this.leadData?.BrokeringInfo?.LastAcceptedDate;

    if (!acceptedDate) {
      return '';
    }

    return acceptedDate instanceof Date ? acceptedDate.toISOString() : acceptedDate;
  }

}
